import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { getDiagnosticQuestions } from '../data/diagnosticQuestionsMultilang';
import { useLanguage } from '../contexts/LanguageContext';

const LRPFramework: React.FC = () => {
  const { language, t } = useLanguage();
  const diagnosticQuestions = getDiagnosticQuestions(language);
  const [activeQuestion, setActiveQuestion] = useState<string | null>(null);

  const [emblaRef] = useEmblaCarousel(
    {
      loop: true,
      align: 'start',
      slidesToScroll: 1,
    },
    [Autoplay({ delay: 4000, stopOnInteraction: true })]
  );

  return (
    <section className="py-20 px-6 relative overflow-hidden" style={{ backgroundColor: 'var(--color-background)' }}>
      {/* Animated gradient backgrounds */}
      <div className="absolute top-0 right-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse" />
      <div className="absolute bottom-1/4 left-1/3 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="inline-block mb-6"
          >
            <span
              className="px-6 py-2 rounded-full text-sm font-semibold glass"
              style={{ color: 'var(--color-accent)' }}
            >
              {t.nav.howWeWork.toUpperCase()}
            </span>
          </motion.div>

          <h2
            className="text-4xl md:text-5xl font-bold mb-6"
            style={{ color: 'var(--color-text-primary)' }}
          >
            {t.lrp.title}
          </h2>
          <p
            className="text-xl max-w-3xl mx-auto"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            {t.lrp.subtitle}
          </p>
        </motion.div>

        {/* Diagnostic Questions Carousel */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <div className="embla overflow-hidden py-4" ref={emblaRef}>
            <div className="embla__container flex gap-4 md:gap-6 px-2">
              {diagnosticQuestions.map((question, index) => (
                <div
                  key={question.id}
                  className="embla__slide"
                >
                  <motion.button
                    onClick={() => setActiveQuestion(activeQuestion === question.id ? null : question.id)}
                    whileHover={{ scale: 1.05, y: -5 }}
                    whileTap={{ scale: 0.98 }}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className={`w-full h-full text-left glass-medium p-4 md:p-6 relative overflow-hidden group transition-all duration-300 ${
                      activeQuestion === question.id ? 'ring-2 ring-purple-500' : ''
                    }`}
                  >
                    {/* Hover gradient */}
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-500/20 to-pink-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                    <div className="relative z-10">
                      {/* Icon */}
                      <motion.div
                        className="w-12 h-12 md:w-16 md:h-16 rounded-2xl flex items-center justify-center text-2xl md:text-3xl mb-2 md:mb-4 glass-strong"
                        whileHover={{ rotate: [0, -10, 10, -10, 0] }}
                        transition={{ duration: 0.5 }}
                        style={{
                          background: activeQuestion === question.id
                            ? 'linear-gradient(135deg, var(--color-primary), var(--color-accent))'
                            : 'rgba(255, 255, 255, 0.1)'
                        }}
                      >
                        {question.icon}
                      </motion.div>

                      {/* Question */}
                      <h4
                        className="text-sm md:text-lg font-bold mb-2"
                        style={{ color: 'var(--color-text-primary)' }}
                      >
                        {question.question}
                      </h4>

                      {/* Reason text */}
                      <AnimatePresence mode="wait">
                        {activeQuestion === question.id ? (
                          <motion.div
                            key="active"
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="overflow-hidden"
                          >
                            <motion.div
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ delay: 0.15 }}
                              className="mt-3 text-sm font-medium leading-relaxed"
                              style={{ color: 'var(--color-accent)' }}
                            >
                              {question.reason}
                            </motion.div>
                          </motion.div>
                        ) : (
                          <motion.div
                            key="inactive"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="mt-3"
                          >
                            <div
                              className="h-1 w-16 rounded-full"
                              style={{ backgroundColor: 'var(--color-accent)' }}
                            />
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </motion.button>
                </div>
              ))}
            </div>
          </div>

          {/* Carousel Indicators */}
          <div className="flex justify-center gap-2 mt-6">
            {Array.from({ length: Math.ceil(diagnosticQuestions.length / 3) }).map((_, index) => (
              <div
                key={index}
                className="w-2 h-2 rounded-full bg-white/30"
              />
            ))}
          </div>
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
        >
          <motion.div
            whileHover={{ scale: 1.02 }}
            className="glass-strong text-center p-10 relative overflow-hidden"
          >
            {/* Animated background gradient */}
            <motion.div
              className="absolute inset-0"
              style={{
                background: 'linear-gradient(135deg, var(--color-primary), var(--color-accent))',
                opacity: 0.9
              }}
              animate={{
                backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
              }}
              transition={{
                duration: 5,
                repeat: Infinity,
                ease: 'linear'
              }}
            />

            <div className="relative z-10">
              <p className="text-xl md:text-2xl font-semibold mb-3 text-white/90">
                {t.lrp.weDoNotSellTools}
              </p>
              <h3 className="text-3xl md:text-5xl font-bold text-white">
                {t.lrp.weDeliverSmartSolutions}
              </h3>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default LRPFramework;
